import React, { useState } from 'react';

import PropTypes from 'prop-types';
import styled from 'styled-components';

import InfoCircle from '~/icons/components/InfoCircle';
import { AlertTriangle } from '~/icons/fabletics';

const CellWrapper = styled.div`
  padding: 10px;
  text-transform: uppercase;
  font-weight: 700;
  background: rgba(39, 174, 96, 0.4);
  border: 3px dotted rgba(39, 174, 96, 0.8);
  box-sizing: border-box;
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 10px;
  ${({ $isAsset }) =>
    $isAsset &&
    `
      background: transparent;
      border-color: rgba(192, 57, 43, 0.8);
    `}
  ${({ $isEmpty }) =>
    $isEmpty &&
    `
      background: rgba(230, 126, 34, 0.8);
      border-color: rgba(230, 126, 34, 0.8);
    `}
`;

const Details = styled.div`
  position: relative;
  z-index: 1;
`;

const DetailHeading = styled.h2`
  color: ${({ theme: { colors } }) => `${colors.white}`};
  font-size: 11px;
  text-decoration: underline;
  margin-bottom: 2px;
  position: relative;
`;

const Detail = styled.div`
  color: ${({ theme: { colors } }) => `${colors.white}`};
  font-size: 10px;
`;

const ColumnIndicator = styled.div`
  position: absolute;
  width: 100%;
  border-top: 2px dashed #ddd;
  top: -18px;
  left: 0;
`;

const ColumnLabel = styled.div`
  position: absolute;
  left: 50%;
  font-size: 10px;
  transform: translateX(-50%);
  background: rgba(255, 255, 255, 0.8);
  padding: 2px;
  margin-top: -10px;
  border-radius: 4px;
  color: #999;
  margin-left: 1px;
`;

const RowIndicator = styled.div`
  position: absolute;
  height: 100%;
  border-right: 2px dashed #ddd;
  right: -18px;
  top: 0;
  z-index: 2;
  ${({ $numColumns }) =>
    $numColumns === 2 &&
    `
      right: -8px;
    `}
`;

const RowLabel = styled.div`
  position: absolute;
  top: 50%;
  font-size: 10px;
  transform: translateX(-50%);
  background: rgba(255, 255, 255, 0.8);
  padding: 2px;
  border-radius: 4px;
  color: #999;
  margin-left: 1px;
`;

const StyledInfoCircle = styled(InfoCircle)`
  position: absolute;
  margin-left: 2px;
  margin-top: -1px;
  cursor: pointer;
`;

const InfoCircleTooltip = styled.div`
  position: absolute;
  top: -3px;
  max-width: 200px;
  background: rgba(0, 0, 0, 0.8);
  padding: 5px;
  left: 82px;
  transform: translate(-50%, -100%);
  text-transform: none;
  border-radius: 3px;
  pointer-events: none;
  font-weight: 500;
  text-align: center;
  opacity: ${({ $isVisible }) => ($isVisible ? 1 : 0)};
  transition: 0.2s opacity linear;
`;

const EmptyMessage = styled.div`
  top: 50%;
  left: 50%;
  font-size: 50px;
  text-align: center;
  transform: translate(-50%, -50%);
  line-height: 90%;
  color: rgba(0, 0, 0, 0.2);
  position: absolute;
`;

function GridGuideCell({ column, item, numColumns, position, row }) {
  const [isTooltipVisible, setTooltipVisible] = useState(false);

  return (
    <CellWrapper
      $isAsset={item.itemType === 'asset'}
      $isEmpty={item.itemType === 'empty'}
    >
      <ColumnIndicator>
        <ColumnLabel>C{column}</ColumnLabel>
      </ColumnIndicator>
      <RowIndicator $numColumns={numColumns}>
        <RowLabel>R{row}</RowLabel>
      </RowIndicator>
      {item.itemType === 'empty' && (
        <EmptyMessage>
          <AlertTriangle />
          Empty Cell
        </EmptyMessage>
      )}
      <Details>
        <DetailHeading>Position Guide</DetailHeading>
        <Detail>Row: {row}</Detail>
        <Detail>Column: {column}</Detail>
        <Detail>Position: {position}</Detail>
      </Details>
      {item?.itemType === 'asset' && item?.cellIndex && (
        <Details>
          <DetailHeading>
            Asset Details
            {!item.isUsingRowColumnConfiguration && (
              <>
                <StyledInfoCircle
                  size={16}
                  onMouseEnter={() => setTooltipVisible(true)}
                  onMouseLeave={() => setTooltipVisible(false)}
                />
                <InfoCircleTooltip $isVisible={isTooltipVisible}>
                  This asset is configured in CMS using position & size. Please
                  use row & column instead.
                </InfoCircleTooltip>
              </>
            )}
          </DetailHeading>
          {item.asset && (
            <>
              <Detail>Container: {item.asset.container?.name}</Detail>
              <Detail>Asset ID: {item.asset.id}</Detail>
              <Detail>
                Segmentation: {item.asset.segmentation?.id} (
                {item.asset.segmentation?.label})
              </Detail>
            </>
          )}
          <Detail>Cells Wide/Size: {item.cellsWide}</Detail>
          <Detail>Cells Tall: {item.cellsTall}</Detail>
          <Detail>Position: {item.position}</Detail>
        </Details>
      )}
    </CellWrapper>
  );
}

GridGuideCell.propTypes = {
  column: PropTypes.number,
  item: PropTypes.object,
  numColumns: PropTypes.number,
  position: PropTypes.number,
  row: PropTypes.number,
};

export default GridGuideCell;
